import type { InventoryConflictRecord } from '@/lib/offline/inventoryCache';
import type { MenuConflictRecord } from '@/lib/offline/menuCache';

type TelemetryPayload = {
  dataset: string;
  tenantSlug: string;
  source: 'cache' | 'network';
  durationMs: number;
  cachedAt?: number | null;
  success: boolean;
  error?: string | null;
};

type ConflictNamespace = 'inventory' | 'menu';
type ConflictPayload = {
  namespace: ConflictNamespace;
  tenantSlug: string;
  conflictCount: number;
  conflicts: unknown[];
  resolvedAt: number;
};

const ENDPOINT = '/api/offline/snapshots/telemetry';
const CONFLICT_ENDPOINT = '/api/offline/conflicts';

const toPayload = (event: TelemetryPayload): string =>
  JSON.stringify({
    dataset: event.dataset,
    tenantSlug: event.tenantSlug,
    source: event.source,
    durationMs: Math.max(0, Math.round(event.durationMs)),
    cachedAt: event.cachedAt ?? null,
    success: event.success,
    error: event.error ?? null,
  });

export const sendOfflineDatasetTelemetry = async (event: TelemetryPayload): Promise<void> => {
  try {
    if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
      const blob = new Blob([toPayload(event)], { type: 'application/json' });
      navigator.sendBeacon(ENDPOINT, blob);
      return;
    }
  } catch {
    // fall through to fetch
  }

  try {
    await fetch(ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: toPayload(event),
    });
  } catch (error) {
    console.warn('[offline] failed to report dataset telemetry', error);
  }
};

const toConflictPayload = (event: ConflictPayload): string =>
  JSON.stringify({
    namespace: event.namespace,
    tenantSlug: event.tenantSlug,
    conflictCount: event.conflictCount,
    conflicts: event.conflicts,
    resolvedAt: event.resolvedAt,
  });

const sendConflictTelemetry = async (payload: ConflictPayload): Promise<void> => {
  const body = toConflictPayload(payload);

  try {
    if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
      const blob = new Blob([body], { type: 'application/json' });
      const flushed = navigator.sendBeacon(CONFLICT_ENDPOINT, blob);
      if (flushed) {
        return;
      }
    }
  } catch {
    // fall through to fetch
  }

  try {
    await fetch(CONFLICT_ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body,
    });
  } catch (error) {
    console.warn('[offline] failed to report conflict telemetry', error);
  }
};

const normalizeConflictRecords = <T extends object>(records: T[]): T[] =>
  records.slice(0, 100).map((record) => ({ ...record }));

export const emitInventoryConflictTelemetry = (
  tenantSlug: string,
  conflicts: InventoryConflictRecord[],
): void => {
  if (!Array.isArray(conflicts) || conflicts.length === 0) {
    return;
  }

  const payload: ConflictPayload = {
    namespace: 'inventory',
    tenantSlug,
    conflictCount: conflicts.length,
    conflicts: normalizeConflictRecords(conflicts),
    resolvedAt: Date.now(),
  };

  void sendConflictTelemetry(payload);
};

export const emitMenuConflictTelemetry = (tenantSlug: string, conflict?: MenuConflictRecord): void => {
  if (!conflict) {
    return;
  }

  const conflictCount = Math.max(1, conflict.changedItemCount ?? 1);
  const payload: ConflictPayload = {
    namespace: 'menu',
    tenantSlug,
    conflictCount,
    conflicts: normalizeConflictRecords([conflict]),
    resolvedAt: Date.now(),
  };

  void sendConflictTelemetry(payload);
};
